import React, { useState, useEffect } from 'react'
import AppToggle from '../../../components/AppToggle'
import AppCheckbox from '../../../components/AppCheckbox'
import {
  getEmailAccountSettings,
  updateEmailAccountSettings,
  testEmailConnection,
} from '../api/emailSettingsApi'

function formatApiErrorMessage(err, fallback = 'Operation failed.') {
  const detail = err?.response?.data?.detail
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    return detail.map((d) => (typeof d === 'string' ? d : d?.msg || JSON.stringify(d))).join(', ')
  }
  if (detail && typeof detail === 'object') {
    return detail.message || detail.msg || JSON.stringify(detail)
  }
  return err?.message || fallback
}

export default function EmailAccountTab() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [feedback, setFeedback] = useState(null)
  const [testResult, setTestResult] = useState(null)
  const [testRecipient, setTestRecipient] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const [form, setForm] = useState({
    isEnabled: false,
    displayName: '',
    emailId: '',
    password: '',
    smtpHost: '',
    smtpPort: '',
    useSsl: true,
    replyToEmail: '',
    defaultCcEmails: '',
    defaultBccEmails: '',
    hasPassword: false,
    lastTestedAt: null,
    lastTestStatus: null,
    lastTestError: null,
  })

  useEffect(() => {
    loadSettings()
  }, [])

  async function loadSettings() {
    setLoading(true)
    setFeedback(null)
    try {
      const data = await getEmailAccountSettings()
      if (data) {
        setForm((prev) => ({
          ...prev,
          isEnabled: Boolean(data.isEnabled),
          displayName: data.displayName || '',
          emailId: data.emailId || '',
          password: '',
          smtpHost: data.smtpHost || '',
          smtpPort: data.smtpPort ? String(data.smtpPort) : '',
          useSsl: data.useSsl ?? true,
          replyToEmail: data.replyToEmail || '',
          defaultCcEmails: data.defaultCcEmails || '',
          defaultBccEmails: data.defaultBccEmails || '',
          hasPassword: Boolean(data.hasPassword),
          lastTestedAt: data.lastTestedAt,
          lastTestStatus: data.lastTestStatus,
          lastTestError: data.lastTestError,
        }))
        if (data.emailId && !testRecipient) {
          setTestRecipient(data.emailId)
        }
      }
    } catch (err) {
      setFeedback({
        type: 'error',
        message: 'Failed to load email account settings: ' + formatApiErrorMessage(err),
      })
    } finally {
      setLoading(false)
    }
  }

  function handleChange(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  async function handleSave(e) {
    e.preventDefault()
    setSaving(true)
    setFeedback(null)

    if (!form.displayName.trim()) {
      setFeedback({ type: 'error', message: 'Display Name is required.' })
      setSaving(false)
      return
    }
    if (!form.emailId.trim()) {
      setFeedback({ type: 'error', message: 'Email ID (Username) is required.' })
      setSaving(false)
      return
    }
    if (!form.hasPassword && !form.password.trim()) {
      setFeedback({ type: 'error', message: 'Please enter the email account or App Password.' })
      setSaving(false)
      return
    }
    if (!form.smtpHost.trim()) {
      setFeedback({ type: 'error', message: 'Outgoing Server (SMTP Host) is required.' })
      setSaving(false)
      return
    }

    try {
      const updated = await updateEmailAccountSettings({
        isEnabled: form.isEnabled,
        displayName: form.displayName.trim(),
        emailId: form.emailId.trim(),
        password: form.password ? form.password.trim() : null,
        smtpHost: form.smtpHost.trim(),
        smtpPort: Number(form.smtpPort || 587),
        useSsl: form.useSsl,
        replyToEmail: form.replyToEmail.trim() || null,
        defaultCcEmails: form.defaultCcEmails.trim() || null,
        defaultBccEmails: form.defaultBccEmails.trim() || null,
      })
      setForm((prev) => ({
        ...prev,
        hasPassword: Boolean(updated?.hasPassword ?? true),
        password: '',
      }))
      setFeedback({ type: 'success', message: 'Email account configuration saved successfully!' })
    } catch (err) {
      setFeedback({
        type: 'error',
        message: 'Error saving settings: ' + formatApiErrorMessage(err),
      })
    } finally {
      setSaving(false)
    }
  }

  async function handleTestConnection() {
    if (!testRecipient.trim()) {
      setTestResult({
        success: false,
        message: 'Please enter a recipient email address to send the test message to.',
      })
      return
    }

    setTesting(true)
    setTestResult(null)
    try {
      const res = await testEmailConnection(testRecipient.trim())
      setTestResult({
        success: Boolean(res?.success),
        message: res?.message || (res?.success ? 'Test email dispatched successfully!' : 'SMTP Test failed.'),
      })
    } catch (err) {
      setTestResult({
        success: false,
        message: formatApiErrorMessage(err, 'SMTP Handshake Connection failed.'),
      })
    } finally {
      setTesting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-16 rounded-3xl border border-outline-variant/25 bg-surface-container-lowest">
        <span className="material-symbols-outlined text-4xl text-primary animate-spin">sync</span>
        <p className="mt-3 text-xs text-on-surface-variant font-medium">Loading Email Account settings...</p>
      </div>
    )
  }

  const isConfigured = Boolean(form.emailId && form.smtpHost && form.hasPassword)

  return (
    <div className="flex flex-col gap-6">
      {/* ── Top Canopy: Header & Action ── */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-outline-variant/15 pb-4">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-primary">
            SYSTEM &amp; COMMUNICATIONS
          </p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-on-surface">
            Tenant Email Credentials &amp; SMTP
          </h1>
          <p className="mt-0.5 text-xs text-on-surface-variant">
            Configure corporate outgoing mailbox credentials for automated alerts and escalations.
          </p>
        </div>

        <div className="flex items-center gap-3 self-start sm:self-auto">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-on-surface-variant">
              {form.isEnabled ? 'Account Enabled' : 'Account Disabled'}
            </span>
            <AppToggle
              checked={form.isEnabled}
              onChange={(nextVal) => handleChange('isEnabled', nextVal)}
              ariaLabel="Toggle Active Email Account"
            />
          </div>
        </div>
      </section>

      {/* ── Main 2-Column Grid ── */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 items-start">
        {/* ── Left Column: Configuration Form (8 cols) ── */}
        <div className="lg:col-span-8 flex flex-col gap-6">
          <div className="rounded-3xl border border-outline-variant/25 bg-surface-container-lowest p-6 shadow-xs">
            {feedback && (
              <div
                className={`flex items-center gap-2.5 p-3.5 mb-5 rounded-2xl text-xs font-semibold ${
                  feedback.type === 'success'
                    ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                    : 'bg-rose-50 text-rose-800 border border-rose-200'
                }`}
              >
                <span className="material-symbols-outlined text-base">
                  {feedback.type === 'success' ? 'check_circle' : 'error'}
                </span>
                <span>{feedback.message}</span>
              </div>
            )}

          <form onSubmit={handleSave} autoComplete="off">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-bold text-on-surface uppercase tracking-wider">
                  Display Name <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  autoComplete="off"
                  className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-3.5 py-2.5 text-xs font-semibold text-on-surface focus:outline-none focus:border-primary placeholder:text-on-surface-variant/40"
                  placeholder="e.g. Boss Global Notifications"
                  value={form.displayName}
                  onChange={(e) => handleChange('displayName', e.target.value)}
                  required
                />
                <span className="text-[10px] text-on-surface-variant">Friendly sender name shown in inbox.</span>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-bold text-on-surface uppercase tracking-wider">
                  Email ID (Username) <span className="text-rose-500">*</span>
                </label>
                <input
                  type="email"
                  autoComplete="off"
                  className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-3.5 py-2.5 text-xs font-semibold text-on-surface focus:outline-none focus:border-primary placeholder:text-on-surface-variant/40"
                  placeholder="e.g. alerts@company.com"
                  value={form.emailId}
                  onChange={(e) => handleChange('emailId', e.target.value)}
                  required
                />
                <span className="text-[10px] text-on-surface-variant">Mailbox address used for outgoing authentication.</span>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-bold text-on-surface uppercase tracking-wider">
                  App Password / Password <span className="text-rose-500">*</span>
                </label>
                <div className="relative flex items-center">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-3.5 py-2.5 pr-10 text-xs font-semibold text-on-surface focus:outline-none focus:border-primary placeholder:text-on-surface-variant/40"
                    placeholder={
                      form.hasPassword ? '•••••••••••••••• (Leave blank to keep existing)' : 'Enter 16-character App Password'
                    }
                    value={form.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                  />
                  <button
                    type="button"
                    tabIndex="-1"
                    className="absolute right-2.5 text-on-surface-variant hover:text-on-surface p-1"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label="Toggle password visibility"
                  >
                    <span className="material-symbols-outlined text-lg">
                      {showPassword ? 'visibility_off' : 'visibility'}
                    </span>
                  </button>
                </div>
                <span className="text-[10px]">
                  {form.hasPassword ? (
                    <span className="text-emerald-600 font-bold">✓ Encrypted password is configured.</span>
                  ) : (
                    <span className="text-on-surface-variant">Use an App Password if 2-Step Verification is active.</span>
                  )}
                </span>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-bold text-on-surface uppercase tracking-wider">
                  Reply-to Email (Optional)
                </label>
                <input
                  type="email"
                  autoComplete="off"
                  className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-3.5 py-2.5 text-xs font-semibold text-on-surface focus:outline-none focus:border-primary placeholder:text-on-surface-variant/40"
                  placeholder="e.g. support@company.com"
                  value={form.replyToEmail}
                  onChange={(e) => handleChange('replyToEmail', e.target.value)}
                />
                <span className="text-[10px] text-on-surface-variant">Destination for replies if different from sender.</span>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-6">
              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-bold text-on-surface uppercase tracking-wider">
                  Outgoing Server (SMTP Host) <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  autoComplete="off"
                  className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-3.5 py-2.5 text-xs font-semibold text-on-surface focus:outline-none focus:border-primary placeholder:text-on-surface-variant/40"
                  placeholder="e.g. smtp.gmail.com"
                  value={form.smtpHost}
                  onChange={(e) => handleChange('smtpHost', e.target.value)}
                  required
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-[11px] font-bold text-on-surface uppercase tracking-wider">
                  Port <span className="text-rose-500">*</span>
                </label>
                <input
                  type="number"
                  autoComplete="off"
                  className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-3.5 py-2.5 text-xs font-semibold text-on-surface focus:outline-none focus:border-primary placeholder:text-on-surface-variant/40"
                  placeholder="587"
                  value={form.smtpPort}
                  onChange={(e) => handleChange('smtpPort', e.target.value)}
                  required
                />
              </div>

              <div className="flex flex-col gap-1.5 justify-center">
                <label className="text-[11px] font-bold text-on-surface uppercase tracking-wider">
                  Security Protocol
                </label>
                <div className="pt-1.5">
                  <AppCheckbox
                    id="email_use_ssl"
                    checked={form.useSsl}
                    onChange={(val) => handleChange('useSsl', val)}
                    label="Use SSL / TLS (587 & 465)"
                  />
                </div>
              </div>
            </div>

            <div>
              <button
                type="submit"
                className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-2.5 text-xs font-bold text-on-primary shadow-xs hover:bg-primary/90 transition disabled:opacity-50"
                disabled={saving}
              >
                <span className={`material-symbols-outlined text-base ${saving ? 'animate-spin' : ''}`}>
                  {saving ? 'sync' : 'save'}
                </span>
                <span>{saving ? 'Saving Settings...' : 'Save Account Configuration'}</span>
              </button>
            </div>
          </form>

          {/* ── Test Email Section ── */}
          <div className="mt-8 rounded-2xl border border-dashed border-outline-variant/30 bg-surface-container-low/30 p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="font-display text-xs font-extrabold text-on-surface">
                Test Outgoing Email Delivery
              </span>
              {form.lastTestedAt && (
                <span className="text-[10px] text-on-surface-variant">
                  Last Tested: <strong>{new Date(form.lastTestedAt).toLocaleString()}</strong>
                </span>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 items-center">
              <input
                type="email"
                autoComplete="off"
                className="w-full sm:max-w-md rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3.5 py-2 text-xs font-semibold text-on-surface focus:outline-none focus:border-primary"
                placeholder="recipient@example.com"
                value={testRecipient}
                onChange={(e) => setTestRecipient(e.target.value)}
              />
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-xl border border-outline-variant/40 bg-surface-container-low px-4 py-2 text-xs font-bold text-on-surface hover:bg-surface-container-high transition shrink-0 disabled:opacity-50"
                onClick={handleTestConnection}
                disabled={testing || !form.emailId}
              >
                <span className={`material-symbols-outlined text-base ${testing ? 'animate-spin' : ''}`}>
                  {testing ? 'sync' : 'send'}
                </span>
                <span>{testing ? 'Testing Handshake...' : 'Send Test Email'}</span>
              </button>
            </div>

            {testResult && (
              <div
                className={`flex items-center gap-2.5 p-3.5 mt-3 rounded-xl text-xs font-semibold ${
                  testResult.success
                    ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                    : 'bg-rose-50 text-rose-800 border border-rose-200'
                }`}
              >
                <span className="material-symbols-outlined text-base">
                  {testResult.success ? 'check_circle' : 'error'}
                </span>
                <span>{testResult.message}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Right Column: TIPS Panel (4 cols, styled like MobileAppTipsCard) ── */}
      <div className="lg:col-span-4 flex flex-col gap-6 sticky top-6">
        <div className="flex flex-col gap-4 rounded-3xl border border-outline-variant/25 bg-surface-container-lowest p-6 shadow-xs">
          {/* Header */}
          <div className="flex items-center gap-2 text-primary font-bold text-sm border-b border-outline-variant/15 pb-4">
            <span className="material-symbols-outlined text-base">info</span>
            <span>TIPS: IMPORTANT CONFIGURATION NOTES</span>
          </div>

          {/* Status Badge */}
          <div className="flex flex-col gap-1 rounded-2xl border border-outline-variant/20 bg-surface-container-low/40 p-3.5">
            <span className="text-[10px] font-black uppercase tracking-wider text-on-surface-variant">
              Account Status
            </span>
            <div className="flex items-center gap-1.5">
              <span
                className={`h-2.5 w-2.5 rounded-full ${
                  form.isEnabled && isConfigured
                    ? 'bg-emerald-500'
                    : 'bg-amber-500'
                }`}
              />
              <span className="text-xs font-black text-on-surface">
                {form.isEnabled && isConfigured
                  ? 'Configured & Ready'
                  : !form.isEnabled
                  ? 'Account Disabled'
                  : 'Pending Credentials'}
              </span>
            </div>
          </div>

          {/* Guidance List matching TEZ ERP */}
          <div className="rounded-2xl border border-outline-variant/15 bg-surface-container-low/30 p-4">
            <ul className="space-y-3 text-[11px] text-on-surface-variant leading-relaxed">
              <li className="flex items-start gap-2">
                <span className="font-bold text-primary shrink-0">1.</span>
                <span>
                  <strong>Gmail App Passwords:</strong> If your organization uses Google Workspace or Gmail with 2-Step Verification, normal passwords will be rejected. Generate a 16-character <em>App Password</em> from your Google Security Settings.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-primary shrink-0">2.</span>
                <span>
                  <strong>Microsoft Office 365:</strong> Set SMTP Host to <code>smtp.office365.com</code> with Port <code>587</code> and TLS enabled. Ensure SMTP AUTH is enabled for the mailbox in Microsoft 365 Admin Center.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-primary shrink-0">3.</span>
                <span>
                  <strong>Ports &amp; Encryption:</strong>
                  <br />• <strong>Port 587:</strong> Uses modern STARTTLS encryption (recommended).
                  <br />• <strong>Port 465:</strong> Uses direct SSL/TLS encryption.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold text-primary shrink-0">4.</span>
                <span>
                  <strong>Tenant Isolation:</strong> Credentials entered here are securely encrypted with AES-256 and stored exclusively inside your tenant database.
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
  )
}

